@if (titleTranslationKey) {
  <app-control-header
    [label]="titleTranslationKey"
    [labelWithColon]="titleWithColon"
    [icon]="labelIcon"
    [index]="index"
    [required]="required"
    [infoTooltip]="infoTooltip"
    [disabled]="isDisabled"
  ></app-control-header>
}

<div
  class="flex w-inherit dropdown-wrapper"
  [ngClass]="{
    'opacity-50': isDisabled && !disabledFilterState,
    'opacity-60': isDisabled && disabledFilterState,
  }"
  [ngbTooltip]="controlTooltip"
  [placement]="controlTooltipPlacement"
>
  <span #contentContainer [class.pointer-events-none]="isDisabled$ | async" (click)="toggleDropdown($event)">
    <ng-content select="[slot='dropdown-button']"></ng-content>
  </span>

  <div
    data-test-id="dropdown-trigger"
    class="flex flex-col flex-1 w-inherit overflow-hidden"
    [ngClass]="{ disabled: isDisabled$ | async }"
  >
    <div #trigger="cdkOverlayOrigin" cdkOverlayOrigin (click)="toggleDropdown($event)">
      @switch (true) {
        @case (!!customTriggerContent) {
          <div #triggerButton>
            <ng-container *ngTemplateOutlet="customTriggerContent" />
          </div>
        }

        @case (!!customTriggerTemplate) {
          <div #triggerButton>
            <ng-container
              *ngTemplateOutlet="
                customTriggerTemplate;
                context: {
                  selectedItem: value,
                  isDropdownOpened,
                  placeholder: (placeholderTranslationKey | translate),
                  isLoading: loading,
                  hasErrors: hasCustomErrors,
                  isDisabled: (isDisabled$ | async),
                  errorTexts,
                }
              "
            />
          </div>
        }

        @case (!contentExists) {
          <app-dropdown-button
            #triggerButton
            [class.gap-x-1.5]="hasSelectedOptionIcon"
            [class.full-view]="doesOptionShouldBeShownFully"
            [class.border-error]="hasCustomErrors"
            [isOpen]="isDropdownOpened"
            [value]="getButtonDisplayValue(value)"
            [pinnedLabel]="getPinnedLabel(value)"
            [iconNextToValue]="value?.iconNextToValue"
            [placeholder]="placeholderTranslationKey | translate"
            [disabled]="isDisabled$ | async"
            [isLoading]="loading"
            [disabledFilterState]="disabledFilterState"
          >
            @if (!loading && hasSelectedOptionIcon) {
              <svg-icon [ngClass]="{ 'w-6 h-6': fixedIconSize }" [src]="value.icon"></svg-icon>
            }
          </app-dropdown-button>
        }
      }
    </div>

    @if (hasCustomErrors) {
      <app-control-errors class="flex-1 mt-1" [errorTexts]="errorTexts"></app-control-errors>
    }

    @if (inputDescriptionTranslationKey) {
      <span class="text-sm text-navy-70 mt-1">{{ inputDescriptionTranslationKey | translate }}</span>
    }
  </div>

  @if (comingSoon) {
    <svg-icon class="ml-7.5" preserveAspectRatio="none" [src]="'coming-soon'" [stretch]="true"></svg-icon>
  }
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="trigger"
  [cdkConnectedOverlayOpen]="isDropdownOpened"
  [cdkConnectedOverlayPanelClass]="overlayPanelClass"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
  [cdkConnectedOverlayPositions]="dropdownPosition || responsiveOverlayPositions"
  (backdropClick)="close()"
>
  <app-dropdown-options-backdrop #openableElement [attr.data-test-id]="dataTestId" [style.width.px]="listWidth">
    @if (searchEnabled) {
      <app-dropdown-search-input
        class="w-full mb-px"
        [formControl]="searchField"
        [placeholder]="searchFieldPlaceholder | translate"
        [autoFocus]="searchAutoFocus"
        (input)="handleSearchInput()"
        (click)="searchFieldClick($event)"
      ></app-dropdown-search-input>
    }

    <perfect-scrollbar #parentScrollRef [style.max-height]="listMaxHeight + 'px'" [config]="{ suppressScrollX: true }">
      @if (unselectOption) {
        <app-dropdown-option (click)="selectItem(undefined)">
          <svg-icon src="clear-form"></svg-icon>
          <!-- TODO: move to i18n -->
          Unselect
        </app-dropdown-option>
      }

      <virtual-scroller
        #scroller
        [parentScroll]="$any(parentScrollRef)?.directiveRef?.elementRef.nativeElement"
        [items]="displayedData"
        [bufferAmount]="30"
        [enableUnequalChildrenSizes]="true"
      >
        @for (item of $any(scroller).viewPortItems; track $index) {
          <div [ngbTooltip]="itemHoverTooltip?.displayCondition(item) ? itemHoverTooltip.templateTooltip : undefined">
            <app-dropdown-option
              [attr.data-test-id]="item.id || getDisplayValue(item)"
              [style.height]="dropdownOptionHeight"
              [class.full-view]="doesOptionShouldBeShownFully"
              [disabled]="getIsValueDisabled(item)"
              [context]="item"
              [action]="selectItemFunc"
              [selected]="compareItemToCurrentValue(item)"
            >
              <ng-container
                *ngTemplateOutlet="customDropdownOptionRef || customOption; context: { thisItem: item }"
              ></ng-container>
            </app-dropdown-option>
          </div>
        }
      </virtual-scroller>

      @if (!displayedData?.length) {
        <span class="empty-list-placeholder">
          {{ emptyListPlaceholder | translate }}
        </span>
      }
    </perfect-scrollbar>

    @if (isAddOptionShown) {
      <app-add-option
        [entityNameText]="entityNameText"
        [value]="searchField.value"
        [disabled]="shouldDisableAddOptionHandler ? shouldDisableAddOptionHandler(searchField.value) : false"
        [loading]="addOptionLoading$ | async"
        (click)="addClick()"
      ></app-add-option>
    }

    @if (bottomDropdownAction && bottomDropdownDisplayed) {
      <app-dropdown-option
        class="border-t border-navy-30"
        [context]="searchValue"
        [style.height]="dropdownOptionHeight"
        [menuAction]="bottomDropdownAction"
      ></app-dropdown-option>
    }

    <span class="dropdown-footer">
      <ng-content select="[slot='dropdown-footer']"></ng-content>
    </span>
  </app-dropdown-options-backdrop>
</ng-template>

<ng-template #customOption let-item="thisItem">
  @if (item.icon) {
    <svg-icon [ngClass]="{ 'w-6 h-6': fixedIconSize }" [src]="item.icon"></svg-icon>
  }
  <span
    class="overflow-hidden overflow-ellipsis whitespace-nowrap"
    [openTooltipWhenOverflowedAndHovered]="true"
    [openOnWidthOverflow]="true"
    [ngbTooltip]="getDisplayValue(item)"
    [placement]="'top'"
  >
    {{ getDisplayValue(item) }}
  </span>
  @if (item.iconNextToValue) {
    <svg-icon [ngClass]="{ 'w-6 h-6': fixedIconSize }" [src]="item.iconNextToValue"></svg-icon>
  }
</ng-template>
